import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import { environment } from '../../environments/environment';
import {
  DocumentQuery,
  DocumentSummary,
  DocumentDetail,
  LookupOption,
  PagedResult,
  SecurityAccess,
  ManualCheckProcessResponse,
} from '../models/document.models';

/** Client for the DocRepoAPI AP document endpoints. */
@Injectable({ providedIn: 'root' })
export class DocumentService {
  private readonly base = `${environment.apiBaseUrl}/api`;

  constructor(private http: HttpClient) {}

  /** Document endpoint base for a category: AR has its own controller; everything else = AP. */
  private docsPath(category?: string): string {
    return category === 'AR' ? `${this.base}/ar/documents` : `${this.base}/documents`;
  }

  /** The signed-in user's DocRepo access (app gate + allowed companies/categories). */
  getAccess(): Observable<SecurityAccess> {
    return this.http.get<SecurityAccess>(`${this.base}/security/access`);
  }

  /** Scans an entity's SharePoint manual-checks folder; returns the .pdf file names. (Role 149 gated.) */
  getManualCheckFiles(entity: string): Observable<{ count: number; files: string[] }> {
    const params = new HttpParams().set('entity', entity);
    return this.http.get<{ count: number; files: string[] }>(`${this.base}/manual-checks/files`, { params });
  }

  /**
   * Imports ALL pending manual checks from SharePoint for BOTH companies (GP read + WebHub rows +
   * file). Not 149-gated server-side: used by the badge button and the check-# search-miss.
   */
  processManualChecks(): Observable<ManualCheckProcessResponse> {
    return this.http.post<ManualCheckProcessResponse>(`${this.base}/manual-checks/process`, { move: true });
  }

  getCompanies(): Observable<LookupOption[]> {
    return this.http.get<LookupOption[]>(`${this.base}/lookups/companies`);
  }

  getCategories(company?: string): Observable<LookupOption[]> {
    let params = new HttpParams();
    if (company && company !== 'All') {
      params = params.set('company', company);
    }
    return this.http.get<LookupOption[]>(`${this.base}/lookups/categories`, { params });
  }

  getClassifications(company?: string, category?: string): Observable<LookupOption[]> {
    let params = new HttpParams();
    if (company && company !== 'All') {
      params = params.set('company', company);
    }
    const path = category === 'AR' ? 'ar-classifications' : 'classifications';
    return this.http.get<LookupOption[]>(`${this.base}/lookups/${path}`, { params });
  }

  /** GL account codes (value=code, label=description) for the GL typeahead, scoped to a company. */
  getGlAccounts(company?: string): Observable<LookupOption[]> {
    let params = new HttpParams();
    if (company && company !== 'All') {
      params = params.set('company', company);
    }
    return this.http.get<LookupOption[]>(`${this.base}/lookups/gl-accounts`, { params });
  }

  /** Party typeahead scoped to a company: vendors for AP, customers for AR (value=#, label=name). */
  getVendors(company?: string, category?: string): Observable<LookupOption[]> {
    let params = new HttpParams();
    if (company && company !== 'All') {
      params = params.set('company', company);
    }
    const path = category === 'AR' ? 'ar-customers' : 'vendors';
    return this.http.get<LookupOption[]>(`${this.base}/lookups/${path}`, { params });
  }

  /** AP approver names (value=label=name) for the approver typeahead, scoped to a company. */
  getApprovers(company?: string): Observable<LookupOption[]> {
    let params = new HttpParams();
    if (company && company !== 'All') {
      params = params.set('company', company);
    }
    return this.http.get<LookupOption[]>(`${this.base}/lookups/approvers`, { params });
  }

  search(query: DocumentQuery): Observable<PagedResult<DocumentSummary>> {
    let params = new HttpParams();
    const add = (key: string, val: unknown) => {
      if (val !== undefined && val !== null && `${val}`.trim() !== '' && val !== 'All') {
        params = params.set(key, `${val}`);
      }
    };
    add('company', query.company);
    add('category', query.category);
    add('classification', query.classification);
    add('vendor', query.vendor);
    add('approver', query.approver);
    add('glAcct', query.glAcct);
    add('dateFrom', query.dateFrom);
    add('dateTo', query.dateTo);
    add('amount', query.amount);
    add('docNumber', query.docNumber);
    add('checkNumber', query.checkNumber);
    add('poNumber', query.poNumber);
    add('sortBy', query.sortBy);
    add('sortDir', query.sortDir);
    add('page', query.page);
    add('pageSize', query.pageSize);
    return this.http.get<PagedResult<DocumentSummary>>(this.docsPath(query.category), { params });
  }

  getDetail(azureDocId: number, category?: string): Observable<DocumentDetail> {
    return this.http.get<DocumentDetail>(`${this.docsPath(category)}/${azureDocId}`);
  }

  /** URL to stream a document file inline (for preview). */
  fileUrl(azureDocId: number, index = 0, category?: string): string {
    return `${this.docsPath(category)}/${azureDocId}/file?index=${index}`;
  }

  /** URL to download a document file as an attachment. */
  fileDownloadUrl(azureDocId: number, index = 0, category?: string): string {
    return `${this.docsPath(category)}/${azureDocId}/file?index=${index}&download=true`;
  }

  /** Fetches a document file's raw text (used to parse & pretty-render XML e-forms). */
  getFileText(azureDocId: number, index = 0, category?: string): Observable<string> {
    return this.http.get(this.fileUrl(azureDocId, index, category), { responseType: 'text' });
  }

  /** Emails a document's file (at index) to a recipient as a real attachment. */
  emailDocument(
    azureDocId: number,
    to: string,
    index = 0,
    message?: string,
    category?: string,
  ): Observable<{ sent: boolean }> {
    return this.http.post<{ sent: boolean }>(
      `${this.docsPath(category)}/${azureDocId}/email`,
      { to, index, message: message ?? '' },
    );
  }
}
